/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactElement, useState, useContext, useEffect, useMemo } from "react";
import { Box, Tooltip as MuiTooltip } from "@mui/material";
import { Badge } from '@chakra-ui/react'
import { Popper, ClickAwayListener, Paper } from "@mui/material";
import { Tooltip, IconButton } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridClasses,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  MdBlock,
  MdOutlineDone,
  MdClose,
  MdOutlineContentCopy,
  MdOutlineArrowOutward,
  MdCalendarToday,
  MdAccessTime,
  MdMoreHoriz,
  MdInfoOutline,
  MdCurrencyExchange,
  MdOutlinePermIdentity,
} from "react-icons/md";

import { ProfileContext } from "../../../../store/ProfileProvider";
import TradeCancelConfirmModal from "../../shared/TradeCancelConfirmModal";
import TradeCompleteConfirmModal from "../../shared/TradeCompleteConfirmModal";
import TradeRejectConfirmModal from "../../shared/TradeRejectConfirmModal";
import SweetAlert2 from "react-sweetalert2";
import DisplayAmountWithDecimals from "../../shared/DisplayAmountWithDecimals";

dayjs.extend(utc);
dayjs.extend(timezone);

type Merchant = { id: string; name: string };
type Subaccount = { id: string; name: string };
type Address = { id: string; address: string; nickname: string };
type Offer = {
  receive_asset: string;
  receive: number;
  original_rate: number;
  margin: number;
  sell_asset: string;
  sell: string;
  expired_text: string;
  offer_id: string;
  expired: string;
  settlement_address: string;
  rate: number;
  original_receive: number;
  status: string;
};

type OfferHistory = Offer[];

type RowData = {
  id: string;
  status: "awaiting" | "pending" | "cancelled" | "completed" | "rejected";
  shortid: string;
  offer_history: OfferHistory;
  accepted_offer: Offer;

  from_amount: number | string;
  from_asset: string;
  from_network: string;
  from_asset_decimal: number;

  fee_amount?: number | string | null;
  fee_asset?: string | null;
  net_amount?: number | string | null;
  net_asset?: string | null;

  to_asset: string;
  to_network: string;
  to_address: Address;
  to_asset_decimal: number;

  created_at: string;
  merchant: Merchant;
  subaccount: Subaccount;

  transactions: any[];
  reason?: string;
  transaction_reference?: string;

  rate?: number | string | null;
  rate_pair?: string | null;
};

const toNum = (v: number | string | null | undefined) => {
  if (v === null || v === undefined || v === "") return null;
  return typeof v === "number" ? v : Number(v);
};

const fmtFiat2 = (v?: number | string | null) => {
  const n = toNum(v);
  return n === null || Number.isNaN(n)
    ? "—"
    : n.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
};

const fmtRate = fmtFiat2;

const customDataGridStyles: Partial<GridClasses> = {
  root: "mui-table-custom",
  columnHeader: "text-start font-bold text-lg",
  row: "",
};

type ActionMenuCellProps = {
  row: any;
  userPermissions: any;
  onCancel: (id: string) => void;
  onComplete: (id: string) => void;
  onReject: (id: string) => void;
};

const ActionMenuCell: React.FC<ActionMenuCellProps> = ({
  row,
  userPermissions,
  onCancel,
  onComplete,
  onReject,
}) => {
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const status = row?.status;
  const id = row?.id;

  const canCancel =
    userPermissions?.cancelTrade === true && status === "awaiting";
  const canComplete =
    userPermissions?.completeTrade === true && status === "pending";
  const canReject =
    userPermissions?.rejectTrade === true && status === "pending";

  const items: {
    key: "complete" | "reject" | "cancel";
    label: string;
    icon: React.ReactNode;
    action: () => void;
  }[] = [];
  if (canComplete)
    items.push({
      key: "complete",
      label: "Mark as Completed",
      icon: <MdOutlineDone className="h-4 w-4" />,
      action: () => onComplete(id),
    });
  if (canReject)
    items.push({
      key: "reject",
      label: "Mark as Reject",
      icon: <MdClose className="h-4 w-4" />,
      action: () => onReject(id),
    });
  if (canCancel)
    items.push({
      key: "cancel",
      label: "Mark as Cancel",
      icon: <MdBlock className="h-4 w-4" />,
      action: () => onCancel(id),
    });

  if (items.length === 0) {
    return (
      <span
        title="No action available"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-400"
      >
        <MdMoreHoriz className="h-5 w-5" />
      </span>
    );
  }

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setOpen((s) => !s);
  };
  const handleClose = () => setOpen(false);

  const hoverTone: Record<"complete" | "reject" | "cancel", string> = {
    complete: "hover:bg-[#ECFDF5] hover:text-[#047857]",
    reject: "hover:bg-[#FEF2F2] hover:text-[#B91C1C]",
    cancel: "hover:bg-[#F3F4F6] hover:text-[#374151]",
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50"
      >
        <MdMoreHoriz className="h-5 w-5" />
      </button>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start" // ← open to the left side
        modifiers={[
          { name: "offset", options: { offset: [0, 8] } }, // small vertical gap
          {
            name: "flip",
            options: { fallbackPlacements: ["top-start", "top-end"] },
          },
          {
            name: "preventOverflow",
            options: { padding: 8, altBoundary: true },
          },
        ]}
        style={{ zIndex: 1500 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper elevation={6} sx={{ borderRadius: 1.25, overflow: "hidden" }}>
            <div className="w-56 divide-y divide-gray-100">
              {items.map((it) => (
                <button
                  key={it.key}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                    it.action();
                  }}
                  className={[
                    "text-slate-700 flex w-full items-center gap-3 px-3.5 py-2.5 text-left text-sm",
                    "transition-colors",
                    hoverTone[it.key],
                  ].join(" ")}
                >
                  <span
                    className={[
                      "grid h-5 w-5 place-items-center rounded-full border text-[13px]",
                      it.key === "complete"
                        ? "border-[#A7F3D0] !text-[#047857]"
                        : it.key === "reject"
                          ? "border-[#FCA5A5] !text-[#B91C1C]"
                          : "border-none !text-[#B45309]",
                    ].join(" ")}
                  >
                    {it.icon}
                  </span>
                  <span className="text-gray-800">{it.label}</span>
                </button>
              ))}
            </div>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

const idPill = (id: string, onCopy: (id: string) => void) => (
  <MuiTooltip title="Copy trade code" arrow>
    <button
      onClick={(e) => {
        e.stopPropagation();
        onCopy(id);
      }}
      className={[
        "py-0.3 inline-flex items-center rounded-full px-2.5",
        "font-mono text-[11px] font-semibold",
        "bg-emerald-100 text-emerald-800 ring-emerald-200 ring-1",
        "hover:bg-emerald-200/70 transition-colors",
      ].join(" ")}
    >
      {id}
      <MdOutlineContentCopy className="ml-1 h-3.5 w-3.5 opacity-60" />
    </button>
  </MuiTooltip>
);

interface TradeStatusItems {
  status: string;
  value: string;
  display: ReactElement;
}

const statusClasses = 'status';

const statusList: TradeStatusItems[] = [
  { status: "awaiting", value: 'awaiting', display: <Badge className={`${statusClasses} status-awaiting`}>Awaiting</Badge> },
  { status: "pending", value: 'pending', display: <Badge className={`${statusClasses} status-pending`}>Pending</Badge> },
  { status: "cancelled", value: 'cancelled', display: <Badge className={`${statusClasses} status-cancelled`}>Cancelled</Badge> },
  { status: "completed", value: 'completed', display: <Badge className={`${statusClasses} status-completed`}>Completed</Badge> },
  { status: "rejected", value: 'rejected', display: <Badge className={`${statusClasses} status-rejected`}>Rejected</Badge> },
];

const TradeList: React.FC<{
  userPermissions: any;
  isLoading: boolean;
  tableData: RowData[];
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: any;
  currentLastKey: string;
  triggerReloadList(): void;
}> = ({
  userPermissions,
  isLoading,
  tableData,
  page,
  pageSize,
  rowCount,
  setPaginationModel,
  currentLastKey,
  triggerReloadList,
}) => {
    const { profileTimeZone } = useContext(ProfileContext);
    const userRole = localStorage.getItem("userRole")?.toLowerCase();

    const [swalProps, setSwalProps] = useState<any>({});

    const [tradeToCancel, setTradeToCancel] = useState<any>(null);
    const [isOpenTradeCancelConfirmModal, setIsOpenTradeCancelConfirmModal] = useState<boolean>(false);

    const [tradeToComplete, setTradeToComplete] = useState<any>(null);
    const [isOpenTradeCompleteConfirmModal, setIsOpenTradeCompleteConfirmModal] = useState<boolean>(false);

    const [tradeToReject, setTradeToReject] = useState<any>(null);
    const [isOpenTradeRejectConfirmModal, setIsOpenTradeRejectConfirmModal] = useState<boolean>(false);

    const formatDate = (ts?: string | number | null) => {
      if (!ts) return "—";

      if (!isNaN(Number(ts))) {
        const num = Number(ts);
        return dayjs(num < 1e12 ? num * 1000 : num)
          .tz(profileTimeZone)
          .format("hh:mmA D MMM YY");
      }

      return dayjs(ts).tz(profileTimeZone).format("hh:mmA D MMM YY");
    };

    const formatAddress = (address?: string): string => {
      if (!address || address.length < 8) return "N/A";
      return `${address.slice(0, 6)}…${address.slice(-4)}`;
    };

    const handleClickCopy = async (tradeId: string) => {
      try {
        await navigator.clipboard.writeText(tradeId);
      } catch (err) {
        console.error("Failed to copy trade ID: ", err);
      }
    };

    const handleClickCancel = (tradeId: any) => {
      const trade = tableData.find((x: any) => x.id === tradeId);
      setTradeToCancel(trade);
      setIsOpenTradeCancelConfirmModal(true);
    };
    const closeTradeCancelConfirmModal = () => {
      setTradeToCancel(null);
      setIsOpenTradeCancelConfirmModal(false);
    };

    const handleClickComplete = (tradeId: any) => {
      const trade = tableData.find((x: any) => x.id === tradeId);
      setTradeToComplete(trade);
      setIsOpenTradeCompleteConfirmModal(true);
    };
    const closeTradeCompleteConfirmModal = () => {
      setTradeToComplete(null);
      setIsOpenTradeCompleteConfirmModal(false);
    };

    const handleClickReject = (tradeId: any) => {
      const trade = tableData.find((x: any) => x.id === tradeId);
      setTradeToReject(trade);
      setIsOpenTradeRejectConfirmModal(true);
    };
    const closeTradeRejectConfirmModal = () => {
      setTradeToReject(null);
      setIsOpenTradeRejectConfirmModal(false);
    };

    const columns: GridColDef[] = [
      {
        field: "status",
        headerName: "Trade",
        minWidth: 260,
        flex: 1.25,
        sortable: false,
        renderCell: (params: GridRenderCellParams<RowData>) => {
          const status = params.value as string;
          const statusItem = statusList.find(item => item.status === status);
          const tip =
            params.row.status === "completed" && params.row.transaction_reference
              ? `Ref: ${params.row.transaction_reference}`
              : params.row.status === "rejected" && params.row.reason
                ? `Reason: ${params.row.reason}`
                : undefined;

          return (
            <div>
              <div className="mt-2 flex items-center gap-2 text-base font-semibold text-gray-800">
                <div>{params.row.from_asset}</div>
                <MdOutlineArrowOutward />
                <div>{params.row.to_asset}</div>
              </div>
              <div className="mt-1 flex items-center gap-2">
                {idPill(params.row.shortid, handleClickCopy)}
                <>
                  {userRole === 'superadmin' &&
                    ((status === 'completed' && params.row.transaction_reference) ||
                      (status === 'rejected' && params.row.reason)) ? (
                    <Tooltip
                      title={status === 'completed' ? "Ref. " + params.row.transaction_reference : "Reason: " + params.row.reason}
                      arrow
                      placement="top"
                    >
                      <div className="cursor-help">
                        {statusItem ? statusItem.display : null}
                      </div>
                    </Tooltip>
                  ) : (
                    statusItem ? statusItem.display : null
                  )}
                </>
              </div>
            </div>
          );
        }
      },

      {
        field: "from_amount",
        headerName: "Trade Amount",
        minWidth: 160,
        flex: 1,
        sortable: false,
        renderCell: (params: GridRenderCellParams<RowData>) => {
          const accepetedOffer = params.row.offer_history ? params.row.offer_history.find((item: Offer) => item.status === "accepted") : null;
          return (
            <div className="flex flex-col items-baseline">
              <div className="mt-2 text-base font-semibold text-gray-800">
                {params.row.from_network === "fiat" ? (
                  <DisplayAmountWithDecimals
                    amount={Number(params.row.from_amount)}
                    currency={params.row.from_asset}
                    decimal={params.row.from_asset_decimal}
                  />
                ) : (
                  params.row.from_amount
                )}
                &nbsp;{params.row.from_asset}
              </div>
              {accepetedOffer &&
                <div className="mt-1 text-xs text-gray-700">
                  {params.row.to_network === "fiat" ? (
                    <DisplayAmountWithDecimals
                      amount={Number(accepetedOffer.receive)}
                      currency={params.row.to_asset}
                      decimal={params.row.to_asset_decimal}
                    />
                  ) : (
                    accepetedOffer.receive
                  )}
                  &nbsp;{params.row.to_asset}
                </div>
              }
            </div>
          )
        }
      },
      {
        field: "to_asset",
        headerName: "Net Received",
        minWidth: 240,
        flex: 1,
        sortable: false,
        renderCell: (params: GridRenderCellParams<RowData>) => {
          const accepetedOffer = params.row.offer_history ? params.row.offer_history.find((item: Offer) => item.status === "accepted") : null;
          const pair = params.row.rate_pair || `${params.row.from_asset}/${params.row.to_asset}`;
          return accepetedOffer ? (
            <div className="leading-snug">
              <div className="mt-2 text-base font-semibold text-gray-800">
                {params.row.to_network === "fiat" ? (
                  <DisplayAmountWithDecimals
                    amount={Number(accepetedOffer.receive)}
                    currency={params.row.to_asset}
                    decimal={params.row.to_asset_decimal}
                  />
                ) : (
                  accepetedOffer.receive
                )}
                &nbsp;{params.row.to_asset}
              </div>

              {/* <div className="mt-1.5 flex items-center gap-2 text-xs text-gray-700">
                <MdInfoOutline className="h-4 w-4 opacity-70" />
                Fee: 0.00001 BTC
              </div> */}

              <div className="mt-1.5 flex items-center gap-2 text-xs text-gray-700">
                <MdCurrencyExchange className="h-4 w-4 opacity-70" />
                <div>
                  {" "}
                  {params.row.from_network === "fiat" ? (
                    <DisplayAmountWithDecimals
                      amount={Number(accepetedOffer.rate)}
                      currency={params.row.from_asset}
                      decimal={params.row.from_asset_decimal}
                    />
                  ) : (
                    accepetedOffer.rate
                  )}
                  &nbsp;{pair} <span>(Rate)</span>
                </div>
              </div>
            </div>
          ) : (null);
        }
      },
      {
        field: "subaccount",
        headerName: "Trader",
        minWidth: 200,
        flex: 0.9,
        sortable: false,
        renderCell: (params) => (
          <div className="leading-snug">
            {params.row.from_network === "fiat" &&
              <>
                <div className="mt-2.5 text-sm font-semibold text-gray-800">
                  {params.row.to_address?.nickname || "N/A"}
                </div>
                <div className="mt-1.5 text-xs text-gray-700">
                  {formatAddress(params.row.to_address?.address)}
                  {params.row.to_network ? ` • ${params.row.to_network}` : ""}
                </div>
              </>
            }
            <div className="mt-1.5 flex items-center gap-1 text-xs text-gray-700">
              <MdOutlinePermIdentity className="h-4 w-4 opacity-70" />
              <div> {params.row.subaccount?.name || params.row.merchant?.name || "—"}</div>
            </div>
          </div>
        )
      },

      {
        field: "created_at",
        headerName: "Dates",
        minWidth: 210,
        flex: 0.9,
        sortable: false,
        renderCell: (params) => {
          return (
            <div className="flex flex-col gap-1.5">
              <MuiTooltip title="Requested" arrow placement="top">
                <div className="mt-2.5 flex items-center gap-2 text-sm font-semibold tracking-tighter text-gray-800">
                  {formatDate(params.row.created_at)}
                </div>
              </MuiTooltip>
              {(params.row.status === "completed" || params.row.status === "rejected") && params.row.processed_date &&
                <MuiTooltip title="Processed" arrow placement="top">
                  <div className="flex items-center gap-2 text-xs text-gray-700">
                    {" "}
                    Updated at: {formatDate(params.row.processed_date)}
                  </div>
                </MuiTooltip>
              }
            </div>
          );
        }
      },
      {
        field: "actions",
        headerName: "Action",
        sortable: false,
        headerAlign: "right",
        align: "right",
        minWidth: 80,
        flex: 0.5,
        cellClassName: "action-menu-cell",
        renderCell: (params) => {
          const { status } = params?.row || {};
          const canCancel = userPermissions.cancelTrade === true && status === "awaiting";
          const canComplete = userPermissions.completeTrade === true && status === "pending";
          const canReject = userPermissions.rejectTrade === true && status === "pending";
          return (canCancel || canComplete || canReject) && (
            <ActionMenuCell
              row={params.row}
              userPermissions={userPermissions}
              onCancel={(id) => handleClickCancel(id)}
              onComplete={(id) => handleClickComplete(id)}
              onReject={(id) => handleClickReject(id)}
            />
          )
        },
      },
    ];

    const filterColumns = () => {
      if (userRole !== "superadmin") {
        const exclude = !userPermissions.cancelTrade ? ["actions"] : [];
        return columns.filter((c) => !exclude.includes(c.field as string));
      }
      return columns;
    };

    return (
      <>
        {isOpenTradeCancelConfirmModal && tradeToCancel && (
          <TradeCancelConfirmModal
            setSwalProps={setSwalProps}
            triggerReload={triggerReloadList}
            isOpen={isOpenTradeCancelConfirmModal}
            dataToProcess={tradeToCancel}
            onClose={closeTradeCancelConfirmModal}
          />
        )}
        {isOpenTradeCompleteConfirmModal && userRole === "superadmin" && tradeToComplete && (
          <TradeCompleteConfirmModal
            setSwalProps={setSwalProps}
            triggerReload={triggerReloadList}
            isOpen={isOpenTradeCompleteConfirmModal}
            dataToProcess={tradeToComplete}
            onClose={closeTradeCompleteConfirmModal}
          />
        )}
        {isOpenTradeRejectConfirmModal && userRole === "superadmin" && tradeToReject && (
          <TradeRejectConfirmModal
            setSwalProps={setSwalProps}
            triggerReload={triggerReloadList}
            isOpen={isOpenTradeRejectConfirmModal}
            dataToProcess={tradeToReject}
            onClose={closeTradeRejectConfirmModal}
          />
        )}

        <Box className="card mt-3 w-full">
          <DataGrid
            sx={{
              "& .MuiDataGrid-row": {
                maxHeight: "120px !important",
                minHeight: "120px !important",
                overflow: "visible",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#FFF !important",
              },
              "& .MuiDataGrid-row.Mui-selected:hover": {
                backgroundColor: "#FFF !important",
              },
              "& .MuiDataGrid-row.Mui-selected": {
                backgroundColor: "#FFF !important",
              },
              "& .MuiDataGrid-cell": {
                lineHeight: "20px",
                padding: "16px 14px",
                alignItems: "baseline",
                overflow: "visible",
              },
              "& .MuiDataGrid-columnHeader": {
                padding: "12px 14px",
                fontWeight: 700,
              },
              "& .MuiTablePagination-input": { marginRight: "-20px" },
              "& .MuiTablePagination-displayedRows": { display: "none" },
              "& .MuiDataGrid-cell.action-menu-cell": {
                overflow: "visible !important",
              },
            }}
            loading={isLoading}
            rows={tableData || []}
            columns={filterColumns()}
            getRowId={(row) => row.id}
            rowCount={rowCount}
            sortingMode="server"
            paginationMode="server"
            pageSizeOptions={[10, 25, 50, 100]}
            rowHeight={120}
            columnHeaderHeight={48}
            paginationModel={{ page: page, pageSize: pageSize }}
            onPaginationModelChange={(newValue) => setPaginationModel(newValue)}
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            classes={customDataGridStyles}
            hideFooter={currentLastKey === "" && page === 0}
            autoHeight
          />
        </Box>

        <SweetAlert2
          {...swalProps}
          customClass={{
            actions: "custom-swal2-popup-action",
            title: "custom-swal2-popup-title",
            confirmButton:
              "custom-swal2-popup-action custom-swal2-popup-action-confirm",
            cancelButton:
              "custom-swal2-popup-action custom-swal2-popup-action-cancel",
          }}
        />
      </>
    );
  };

export default TradeList;
